import { useState, useMemo } from 'react';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { updateUser, auth } from '../firebase/firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import countryList from 'react-select-country-list';
import { validateTaxId, getIfTaxIdIsRequired } from '../utils/taxId';

const AddShippingAddress = () => {
  const [validated, setValidated] = useState(false);
  const [country, setCountry] = useState({
    label: 'United States',
    value: 'US',
  });

  // `recipient_tax_id` is required for shipping addresses to Brazil, Chile, and Mexico.
  // This field holds the recipient's tax identification number (CPF/CNPJ for Brazil, RUT for Chile, and RFC for Mexico).
  const isTaxIdRequired = getIfTaxIdIsRequired(country?.value);

  const [user] = useAuthState(auth);
  const { t } = useTranslation();

  const countries = useMemo(() => countryList().getData(), []);

  const onCountryChange = (value) => {
    setCountry(value);
  };

  const handleSubmit = (event) => {
    const form = event.currentTarget;
    event.preventDefault();

    const isTaxIdValid = validateTaxId(
      event.target.recipientTaxId?.value,
      country?.value
    );

    if (form.checkValidity() === false || !isTaxIdValid) {
      event.stopPropagation();
    } else {
      const data = {
        shipping_address: {
          apartment: event.target?.apartment?.value,
          city: event.target.city.value,
          country_code: country?.value,
          first_name: event.target.firstName.value,
          last_name: event.target.lastName.value,
          phone: event.target.phone.value,
          ...(isTaxIdRequired
            ? { recipient_tax_id: event.target.recipientTaxId.value }
            : {}),
          state_code: event.target.state.value,
          street_address: event.target.streetAddress.value,
          zip_code: event.target.zipCode.value,
        },
      };

      updateUser({
        uid: user?.uid,
        data,
        email: user?.email,
      });
    }

    setValidated(true);
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <h2>{t('dashboardScreen.whatsYourShippingAddress')}</h2>
        <p style={{ marginBottom: 32 }}>
          {t('dashboardScreen.yourBookHalfwayThere')}
        </p>
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <Row>
            <Form.Group as={Col} md="6" className="mb-3" controlId="firstName">
              <Form.Label>{t('dashboardScreen.firstName')}</Form.Label>
              <Form.Control required type="text" autoComplete="given-name" />
            </Form.Group>
            <Form.Group as={Col} md="6" className="mb-3" controlId="lastName">
              <Form.Label>{t('dashboardScreen.lastName')}</Form.Label>
              <Form.Control required type="text" autoComplete="family-name" />
            </Form.Group>
          </Row>
          <Row>
            <Form.Group as={Col} md="6" className="mb-3" controlId="country">
              <Form.Label>{t('common.country')}</Form.Label>
              <Select
                options={countries}
                value={country}
                onChange={onCountryChange}
              />
            </Form.Group>
            <Form.Group as={Col} md="6" className="mb-3" controlId="phone">
              <Form.Label>{t('dashboardScreen.phoneNumber')}</Form.Label>
              <Form.Control required type="text" autoComplete="tel" />
              <Form.Control.Feedback type="invalid">
                {t('dashboardScreen.provideNumber')}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>
          <Row>
            <Form.Group
              as={Col}
              md="6"
              className="mb-3"
              controlId="streetAddress"
            >
              <Form.Label>{t('dashboardScreen.streetAddress')}</Form.Label>
              <Form.Control
                required
                type="text"
                maxLength={30}
                autoComplete="address-line1"
              />
              <Form.Control.Feedback type="invalid">
                {t('dashboardScreen.provideStreetAddress')}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} md="6" className="mb-3" controlId="apartment">
              <Form.Label>{t('dashboardScreen.aptOptional')}</Form.Label>
              <Form.Control type="text" autoComplete="address-line2" />
            </Form.Group>
          </Row>
          <Row>
            <Form.Group as={Col} md="6" className="mb-3" controlId="city">
              <Form.Label>{t('common.city')}</Form.Label>
              <Form.Control
                type="text"
                required
                autoComplete="address-level2"
              />
              <Form.Control.Feedback type="invalid">
                {t('dashboardScreen.provideCity')}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} md="3" className="mb-3" controlId="state">
              <Form.Label>{t('common.state')}</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. NY"
                maxLength={3}
                autoComplete="address-level1"
              />
              <Form.Control.Feedback type="invalid">
                {t('dashboardScreen.provideState')}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} md="3" className="mb-3" controlId="zipCode">
              <Form.Label>{t('dashboardScreen.zipCode')}</Form.Label>
              <Form.Control type="text" required autoComplete="postal-code" />
              <Form.Control.Feedback type="invalid">
                {t('dashboardScreen.provideZipCode')}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>
          {isTaxIdRequired && (
            <Row>
              <Form.Group
                as={Col}
                md="6"
                className="mb-3"
                controlId="recipientTaxId"
              >
                <Form.Label>{t('dashboardScreen.recipientTaxId')}</Form.Label>
                <Form.Control type="text" required autoComplete="tax-id" />
                <Form.Control.Feedback type="invalid">
                  {t('dashboardScreen.provideRecipientTaxId')}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>
          )}
          <Col className="d-flex justify-content-center mt-4">
            <Button variant="dark" className="button-update" type="submit">
              {t('common.submit')}
            </Button>
          </Col>
        </Form>
      </div>
    </>
  );
};

export default AddShippingAddress;
